import type { ModelCreditPricing } from '../../utils/modelCreditPricing';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isPrimaryChatModel } from '../../utils/modelCapabilityLabels';

export interface Model {
  /** 当前提供商的用户组有效价目；随模型目录刷新替换。 */
  creditPricing?: ModelCreditPricing;
  id: string;
  name: string;
  provider?: string; // 模型所属的提供商
  supportsImage?: boolean;
  isImageGeneration?: boolean; // 图片生成模型（如 dall-e-3, flux 等）
  endpointType?: number; // 1=对话,2=向量,3=重排,4=生图,5=TTS,6=STT,7=视频
  // BoCloudAI Platform 透传字段(2026-05-03 起下发):
  //   capabilities — HuggingFace pipeline_tag csv,SDK 内 visionRouting/videoRouting/
  //                  generationModels 自动消费(本地 host 不需要再实现路由)
  //   maxContext   — Platform 给定的上下文长度,SDK getContextWindowForModel 自动读
  //                  → autoCompact 阈值随之确定,本地不再 hardcode
  //   isPrimary    — 是否主对话模型;Cowork 选择器只展示主模型,
  //                  Settings 提供商详情展示全部(含辅助生图/视觉/视频专项模型)
  // 字段缺失(本地配置 / 旧 platform / 自定义 provider)时 isPrimary 视为 true 全展示,
  // 保持向后兼容。
  capabilities?: string[];
  maxContext?: number;
  isPrimary?: boolean;
}

interface ModelState {
  selectedModel: Model;
  availableModels: Model[];
}

const initialState: ModelState = {
  selectedModel: { id: '', name: '' },
  availableModels: [],
};

const modelSlice = createSlice({
  name: 'model',
  initialState,
  reducers: {
    setSelectedModel: (state, action: PayloadAction<Model>) => {
      state.selectedModel = action.payload;
    },
    setAvailableModels: (state, action: PayloadAction<Model[]>) => {
      state.availableModels = action.payload;
      // 同步选中模型信息，确保名称与最新配置一致
      if (action.payload.length > 0) {
        const matchedModel = action.payload.find(m => m.id === state.selectedModel.id && m.provider === state.selectedModel.provider);
        if (matchedModel) {
          state.selectedModel = matchedModel;
        } else {
          // 当前选中的模型不在新列表里 → 优先选第一个主对话模型,避免误把生图/视觉副模型
          // 当主模型选(用户截图反馈:之前 dropdown 把 image-01 这种纯生图模型当可选项)。
          // 全是副模型的极端情况下退回 models[0]。
          state.selectedModel = action.payload.find(m => isPrimaryChatModel(m)) || action.payload[0];
        }
      }
    },
  },
});

export const { setSelectedModel, setAvailableModels } = modelSlice.actions;
export default modelSlice.reducer;
